import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { appSelector } from "../../redux/selector";
import { userSelector } from "../../user-redux/selector";
import Checkbox from "@mui/material/Checkbox";
import {
  getCandidateDetailAction,
  getCandidatesAction,
  getActiveCandidatesAction,
  selectedCandidateAction,
  updateCandidateStatusAction,
} from "../../redux/action";
import { toast } from "react-toastify";
import Key from "../../../clientVariables.json";
import LoadingSkeleton from "../../loadingSkeletons/LoadingList";
import imageSrc from "../../images/Nocandidate.png";
import {
  Avatar,
  Box,
  Button,
  List,
  ListItemButton,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
  Pagination,
  Select,
  MenuItem,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Toolbar,
  IconButton,
  Divider,
  Alert,
  TextField,
} from "@mui/material";
import {
  DescriptionOutlined,
  DifferenceOutlined,
  PersonOutlineRounded,
  PersonOffOutlined as PersonOffOutlinedIcon,
  PersonRemove as PersonRemoveIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router";

const predefinedColors = [
  "#2196F3",
  "#4CAF50",
  "#FFC107",
  "#E91E63",
  "#673AB7",
  "#FF5722",
];

function getRandomColor() {
  // Choose a random color from the predefinedColors array
  const randomIndex = Math.floor(Math.random() * predefinedColors.length);
  return predefinedColors[randomIndex];
}

function getContrastingTextColor(backgroundColor) {
  // Calculate the brightness of the background color to determine the text color
  const hexColor = backgroundColor.replace(/^#/, "");
  const r = parseInt(hexColor.substring(0, 2), 16);
  const g = parseInt(hexColor.substring(2, 4), 16);
  const b = parseInt(hexColor.substring(4, 6), 16);
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;

  // Choose white or black text color based on the background brightness
  return brightness > 128 ? "#000000" : "#FFFFFF";
}

const itemsScroll = [1, 2, 3, 4];

const AdminCandidatesList = ({
  searchTerm,
  paginationResetKey,
  setOpenFileUpload,
  setUploadContext,
  setNoCandidateFound,
  selectedItems,
  handleToggleSelection,
  onCandidateSwitch,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { selectedCandidate, activeCandidates, activeCandLoading, candidateDetails } =
    useSelector(appSelector);
  const { user } = useSelector(userSelector);
  const [firstTimeLoading, setFirstTimeLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [hoverIndex, setHoverIndex] = useState(null);
  const [open, setOpen] = useState(false)
  const label = { inputProps: { "aria-label": "Checkbox demo" } };
  const listScrollRef = useRef(null);

  const candidateAPIRequest = useRef(false);
  const hasAutoSelectedRef = useRef(false);

  const isMobileSmall = useMediaQuery("(max-width: 479px)");
  const isMobileLarge = useMediaQuery(
    "(min-width: 480px) and (max-width: 767px)"
  );
  const isTabletSmall = useMediaQuery(
    "(min-width: 768px) and (max-width: 991px)"
  );
  const isTabletLarge = useMediaQuery(
    "(min-width: 992px) and (max-width: 1199px)"
  );
  const isDesktopXS = useMediaQuery(
    "(min-width: 992px) and (max-width: 1199px)"
  );
  const isDesktopSmall = useMediaQuery(
    "(min-width: 1200px) and (max-width: 1439px)"
  );
  const isDesktopMedium = useMediaQuery(
    "(min-width: 1440px) and (max-width: 1919px)"
  );
  const isDesktopLarge = useMediaQuery("(min-width: 1920px)");

  useEffect(() => {
    if (user) {
      // setFirstTimeLoading(true);
      // Get initial candidates when component mounts
      const userId = user?.usertype == 13 ? (user?.ca_id != null && user?.ca_id != 0) ? user?.ca_id : user?.id : user?.id;
      // Fetch all candidates at once
      dispatch(getActiveCandidatesAction(userId, 1, 500));
    }
  }, [user]); // Only depend on user changes

  useEffect(() => {
    if (activeCandidates?.length > 0) {
      setFirstTimeLoading(false);
    }
  }, [activeCandidates]);

  useEffect(() => {
    if (!activeCandidates?.length) {
      hasAutoSelectedRef.current = false;
      return;
    }
    // Always land on page 1 / first candidate when opening this screen
    // (do not keep previously selected candidate from Redux after navigating away).
    if (hasAutoSelectedRef.current) return;

    const firstCandidate = activeCandidates[0];
    hasAutoSelectedRef.current = true;
    setCurrentPage(1);
    if (listScrollRef.current) listScrollRef.current.scrollTop = 0;

    // Same candidate already selected (list was reset/reloaded on page enter or
    // "already exists"). Calling onCandidateSwitch sets detailsLoading=true, but
    // getCandidateDetailAction returns cache so resume API never runs → stuck skeleton.
    const firstId = String(firstCandidate?.candidate_id);
    if (
      firstId === String(selectedCandidate?.candidate_id) ||
      firstId === String(candidateDetails?.candidate_id)
    ) {
      dispatch(selectedCandidateAction(firstCandidate));
      return;
    }

    onCandidateSwitch?.();
    dispatch(selectedCandidateAction(firstCandidate));
    dispatch(getCandidateDetailAction(firstCandidate.candidate_id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCandidates?.length, dispatch]);

  const avatarColors = [
    "#C5CAE9",
    "#DCEDC8",
    "#FFECB3",
    "#F8BBD0",
    "#C8E6C9",
    "#FFD180",
    "#BBDEFB",
    "#FFCC80",
    "#E6EE9C",
    "#FFD180",
  ];
  const stringAvatar = (name) => {
    const letter = name[0]?.toUpperCase() || "A";
    const bg = avatarColors[letter.charCodeAt(0) % avatarColors.length];
    return {
      sx: {
        bgcolor: bg,
        fontSize: isDesktopLarge ? 14 : isDesktopMedium ? 13 : 12,
        height: 36,
        width: 36,
        color: "#000",
      },
      children: name
        .split(" ")
        .map((n) => n[0])
        .slice(0, 2)
        .join("")
        .toUpperCase(),
    };
  };
  const hoverIconStyles = {
    height: isDesktopLarge
      ? "20px"
      : isDesktopMedium
        ? "18px"
        : isDesktopSmall
          ? "16px"
          : "13px",
    width: isDesktopLarge
      ? "20px"
      : isDesktopMedium
        ? "18px"
        : isDesktopSmall
          ? "16px"
          : "13px",
    color: "#616161",
    "&:hover": {
      bgcolor: "#F5F5F5",
      color: "#424242",
    },
  };

  // const sortedCandidates = [...activeCandidates].sort((a, b) =>
  //   a.candidate_name.localeCompare(b.candidate_name)

  // );

  // Remove duplicates and filter candidates
  const filteredCandidates = searchTerm
    ? [...new Map(activeCandidates?.map(item => [item.candidate_id, item])).values()]
      .filter((candidate) =>
        (candidate.candidate_name?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
        (candidate.role?.toLowerCase() || '').includes(searchTerm.toLowerCase())
      )
    : [...new Map(activeCandidates?.map(item => [item.candidate_id, item])).values()] || [];

  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const paginatedCandidates = filteredCandidates.slice(startIndex, endIndex);

  // Reset to first page when search term changes
  useEffect(() => {
    setCurrentPage(1);
  
    if (!filteredCandidates[0]) return;
  
    const firstCandidate = filteredCandidates[0];
  
    if (
      String(firstCandidate.candidate_id) ===
      String(selectedCandidate?.candidate_id)
    ) {
      return;
    }
  
    // console.log("SEARCH EFFECT", {
    //   searchTerm,
    //   firstCandidate: firstCandidate.candidate_id,
    //   selectedCandidate: selectedCandidate?.candidate_id,
    // });
  
    onCandidateSwitch?.();
    dispatch(selectedCandidateAction(firstCandidate));
    dispatch(getCandidateDetailAction(firstCandidate.candidate_id));
  }, [searchTerm, paginationResetKey]);

  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(filteredCandidates.length / rowsPerPage));
    if (currentPage > totalPages) {
      setCurrentPage(1);
    }
  }, [filteredCandidates.length, rowsPerPage, currentPage]);

  const handlePageChange = (_, value) => {
    setCurrentPage(value);
    if (listScrollRef.current) listScrollRef.current.scrollTop = 0;

    const newPageCandidates = filteredCandidates.slice(
      (value - 1) * rowsPerPage,
      value * rowsPerPage
    );
    if (newPageCandidates.length > 0) {
      const firstCandidate = newPageCandidates[0];
      // console.log(
      //   "SEARCH EFFECT",
      //   "search =", searchTerm,
      //   "firstCandidate =", filteredCandidates[0]?.candidate_id,
      //   "selected =", selectedCandidate?.candidate_id
      // );
      onCandidateSwitch?.();
      dispatch(selectedCandidateAction(firstCandidate));
      dispatch(getCandidateDetailAction(firstCandidate.candidate_id));
    }
  };

  useEffect(() => {
    if (!activeCandLoading && activeCandidates.length <= 0) {
      setOpen(true);
    } else if (filteredCandidates.length > 0) {
      setOpen(false);
    }
  }, [activeCandLoading, filteredCandidates.length]);

  const handleCloseDialog = () => {
    setOpen(false);
    navigate("/admin-dashboard");

  };

  return activeCandLoading ? (
    <LoadingSkeleton />
  ) : (
    <>
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Box
          ref={listScrollRef}
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            overflowX: 'hidden',
            pr: 0.5,
          }}
        >
          <List sx={{ padding: 0 }}>
            {paginatedCandidates.length === 0 && searchTerm ? (
              // Show this when search returns no results
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body2" color="text.secondary">
                  No candidates found matching "{searchTerm}"
                </Typography>
              </Box>
            ) : (
              paginatedCandidates.map((candidate, index) => (
                <ListItemButton
                  key={index}
                  selected={candidate.candidate_id === selectedCandidate?.candidate_id}
                  onMouseEnter={() => setHoverIndex(index)}
                  onMouseLeave={() => setHoverIndex(null)}
                  onClick={() => {
                    if (
                      String(candidate.candidate_id) ===
                      String(selectedCandidate?.candidate_id)
                    ) {
                      return;
                    }
                    onCandidateSwitch?.();
                    // console.log(
                    //   "CLICK SELECT",
                    //   candidate.candidate_id
                    // );
                    //console.log("CLICK SELECT", candidate.candidate_id);
                    dispatch(selectedCandidateAction(candidate));
                    dispatch(getCandidateDetailAction(candidate.candidate_id));
                  }}
                  sx={{
                    padding: isDesktopLarge
                      ? "10px 20px"
                      : isDesktopMedium
                        ? "8px 16px"
                        : isDesktopSmall
                          ? "6px 12px"
                          : "6px 12px",
                  }}
                >
                  <Box display="flex" gap={2} alignItems="center" width="100%">
                    {hoverIndex === index || selectedItems.includes(candidate.candidate_id) ? (
                      <Checkbox
                        checked={selectedItems.includes(candidate.candidate_id)}
                        onChange={(e) => {
                          e.stopPropagation();
                          handleToggleSelection(candidate.candidate_id);
                        }}
                        onClick={(e) => e.stopPropagation()}
                      />
                    ) : (
                      <Avatar
                        alt={candidate.candidate_name}
                        src={candidate.image ? `${Key.aws_bucket_profile}/attachments/user_${candidate.user_id}/profileimages/${candidate.image}` : null}
                        {...stringAvatar(candidate.candidate_name)}
                      />
                    )}
                    <Stack>
                      <Typography
                        sx={{
                          fontSize: isDesktopXS ? "10px" : "12px",
                          fontWeight: 600,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          maxWidth: 160,
                        }}
                      >
                        {candidate.candidate_name}
                      </Typography>
                      <Typography sx={{ fontSize: isDesktopXS ? "9px" : "11px" }}>
                        {candidate.role}
                      </Typography>
                    </Stack>
                  </Box>
                </ListItemButton>
              ))
            )}
            {loading &&
              Array.from({ length: 3 }).map((_, index) => (
                <ListItemButton key={index}>
                  <Skeleton variant="circular" width={40} height={40} />
                  <Stack sx={{ ml: 2, width: "100%" }}>
                    <Skeleton width="60%" height={20} />
                    <Skeleton width="40%" height={15} />
                  </Stack>
                </ListItemButton>
              ))}
          </List>
        </Box>
        {filteredCandidates.length > 10 && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              p: 1,
              borderTop: "1px solid rgba(0, 0, 0, 0.12)",
              bgcolor: "#fff",
            }}
          >
            <Pagination
              count={Math.ceil(filteredCandidates.length / rowsPerPage) || 1}
              page={currentPage}
              onChange={handlePageChange}
              color="primary"
              size="small"
              sx={{ display: "flex", justifyContent: "center", mb: 1 }}
              siblingCount={0}
              boundaryCount={1}
            />
            {filteredCandidates.length >= 10 && (
              <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", px: 2 }}>
                <Typography variant="body2" sx={{ mr: 1 }}>
                  Rows per page:
                </Typography>
                <Select
                  size="small"
                  variant="standard"
                  value={rowsPerPage}
                  onChange={(e) => {
                    const newRowsPerPage = parseInt(e.target.value, 10);
                    setRowsPerPage(newRowsPerPage);
                    if (listScrollRef.current) listScrollRef.current.scrollTop = 0;

                    const selectedId =
                      selectedCandidate?.candidate_id ||
                      candidateDetails?.candidate_id;
                    const selectedIndex = filteredCandidates.findIndex(
                      (candidate) =>
                        String(candidate.candidate_id) === String(selectedId)
                    );

                    if (selectedIndex >= 0) {
                      setCurrentPage(
                        Math.floor(selectedIndex / newRowsPerPage) + 1
                      );
                      return;
                    }

                    setCurrentPage(1);
                    const firstCandidate = filteredCandidates[0];
                    if (!firstCandidate) return;

                    const firstId = String(firstCandidate.candidate_id);
                    if (
                      firstId === String(selectedCandidate?.candidate_id) ||
                      firstId === String(candidateDetails?.candidate_id)
                    ) {
                      dispatch(selectedCandidateAction(firstCandidate));
                      return;
                    }

                    onCandidateSwitch?.();
                    dispatch(selectedCandidateAction(firstCandidate));
                    dispatch(
                      getCandidateDetailAction(firstCandidate.candidate_id)
                    );
                  }}
                  sx={{ width: 60 }}
                >
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={25}>25</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                </Select>
              </Box>
            )}
          </Box>
        )}
      </Box>
      <Dialog
        open={open}
        fullWidth
        onClose={handleCloseDialog}
        maxWidth="xs"
        sx={{ "& .MuiDialog-paper": { width: "400px" } }} >
        <>
          <DialogTitle sx={{ textAlign: "center" }}>
            <img
              src={imageSrc}
              alt="Placeholder"
              style={{
                width: "100%",
                objectFit: "contain",
                borderRadius: "8px",
                marginBottom: "8px",
              }}
            />
          </DialogTitle>
          <DialogContent>
            <Typography variant="body1">
              No Candidate data was found based on the current
              search criteria.
            </Typography>
          </DialogContent>
        </>
        <DialogActions>
          <Button
            onClick={() => {
              setNoCandidateFound(false);
              setOpen(false)
              setTimeout(() => {
                setUploadContext("add_candidates");
                setOpenFileUpload(true);
              }, 100);
            }}
            color="primary"
            variant="outlined"
          >
            Add Candidate
          </Button>
          <Button
            onClick={handleCloseDialog}
            color="primary"
            variant="outlined"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog >
    </>
  );
};

export default AdminCandidatesList;
